import { supabase } from '@/integrations/supabase/client';

// Types
export type AdminRole = 'super_admin' | 'finance_admin' | 'support_admin';
export type AdminStatus = 'active' | 'suspended' | 'pending';

export interface AdminUser {
  id: string;
  user_id: string;
  email: string;
  name: string | null;
  role: AdminRole;
  status: AdminStatus;
  last_login_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface AdminInvite {
  id: string;
  email: string;
  invited_by: string | null;
  role: AdminRole;
  token: string;
  expires_at: string;
  accepted_at: string | null;
  created_at: string;
}

export interface AdminAuditLogEntry {
  id: string;
  admin_user_id: string | null;
  action_type: string;
  target_type: string | null;
  target_id: string | null;
  before_json: Record<string, unknown> | null;
  after_json: Record<string, unknown> | null;
  metadata: Record<string, unknown>;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
}

export interface AdminSettings {
  beta_mode: boolean;
  pause_automated_emails: boolean;
  session_timeout_minutes: number;
  email_send_window_start: number;
  email_send_window_end: number;
}

// Check if current user is an admin
export async function checkIsAdmin(): Promise<{ isAdmin: boolean; role: AdminRole | null; adminUser: AdminUser | null }> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { isAdmin: false, role: null, adminUser: null };

  const { data, error } = await supabase
    .from('admin_users')
    .select('*')
    .eq('user_id', user.id)
    .eq('status', 'active')
    .single();

  if (error || !data) {
    return { isAdmin: false, role: null, adminUser: null };
  }

  return { 
    isAdmin: true, 
    role: data.role as AdminRole, 
    adminUser: data as unknown as AdminUser 
  };
}

// Validate admin email domain
export function isValidAdminEmail(email: string): boolean {
  return email.endsWith('@orbitcrm.com') || email === 'rennanand@icloud.com';
}

// Log admin action
export async function logAdminAction(params: {
  action_type: string;
  target_type?: string;
  target_id?: string;
  before_json?: Record<string, unknown>;
  after_json?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
}): Promise<void> {
  const { data: adminCheck } = await supabase
    .from('admin_users')
    .select('id')
    .eq('user_id', (await supabase.auth.getUser()).data.user?.id || '')
    .single();

  if (!adminCheck) return;

  await supabase.from('admin_audit_log').insert([{
    admin_user_id: adminCheck.id,
    action_type: params.action_type,
    target_type: params.target_type || null,
    target_id: params.target_id || null,
    before_json: (params.before_json || null) as unknown as null,
    after_json: (params.after_json || null) as unknown as null,
    metadata: (params.metadata || {}) as unknown as Record<string, never>,
    user_agent: navigator.userAgent,
  }]);
}

// Fetch audit log
export async function fetchAuditLog(options?: {
  limit?: number;
  targetType?: string;
  targetId?: string;
}): Promise<AdminAuditLogEntry[]> {
  let query = supabase
    .from('admin_audit_log')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(options?.limit || 100);

  if (options?.targetType) {
    query = query.eq('target_type', options.targetType);
  }
  if (options?.targetId) {
    query = query.eq('target_id', options.targetId);
  }

  const { data, error } = await query;
  if (error) throw error;
  return (data || []) as unknown as AdminAuditLogEntry[];
}

// Fetch admin settings
export async function fetchAdminSettings(): Promise<AdminSettings> {
  const { data, error } = await supabase
    .from('admin_settings')
    .select('key, value');

  if (error) throw error;

  const settings: AdminSettings = {
    beta_mode: true,
    pause_automated_emails: false,
    session_timeout_minutes: 30,
    email_send_window_start: 8,
    email_send_window_end: 22,
  };

  for (const row of data || []) {
    const key = row.key as keyof AdminSettings;
    const value = row.value;
    if (key in settings) {
      if (typeof settings[key] === 'boolean') {
        (settings as unknown as Record<string, boolean | number>)[key] = value === 'true' || value === true;
      } else if (typeof settings[key] === 'number') {
        (settings as unknown as Record<string, boolean | number>)[key] = Number(value);
      }
    }
  }

  return settings;
}

// Update admin setting
export async function updateAdminSetting(key: string, value: unknown): Promise<void> {
  const { data: adminCheck } = await supabase
    .from('admin_users')
    .select('id')
    .eq('user_id', (await supabase.auth.getUser()).data.user?.id || '')
    .single();

  await supabase
    .from('admin_settings')
    .upsert({
      key,
      value: JSON.stringify(value),
      updated_by: adminCheck?.id,
      updated_at: new Date().toISOString(),
    });

  await logAdminAction({
    action_type: 'setting_updated',
    target_type: 'admin_settings',
    target_id: key,
    after_json: { [key]: value },
  });
}

// Mask PII
export function maskEmail(email: string): string {
  const [local, domain] = email.split('@');
  if (!domain) return '***@***';
  const maskedLocal = local.length > 2 
    ? local[0] + '***' + local[local.length - 1]
    : '***';
  return `${maskedLocal}@${domain}`;
}

export function maskPhone(phone: string): string {
  if (!phone || phone.length < 4) return '****';
  return '***-***-' + phone.slice(-4);
}
